// Guided-intake content for the "Nieuwe aanvraag" wizard.
//
// All values are seeded from the example dossier SHW_2026_100_1 (the ADHD
// "language preferences" study) with PII removed. See SHW_2026_100_1.md at the
// repo root. The AI-assistant tips are distilled from the EA SHW Tip Sheets
// knowledge base (Tip Sheets 1–16). This is fictional prototype data.

export type FieldType = "text" | "textarea" | "select" | "radio";

export interface IntakeField {
  id: string;
  label: string;
  type: FieldType;
  value: string;
  options?: string[];
  /** Shows an "AI ingevuld" badge: value was drafted from the uploaded docs. */
  aiPrefilled?: boolean;
  /** Small helper line under the label. */
  hint?: string;
  /**
   * Pre-filled explanation ("toelichting") text per answer option, for radio
   * questions. Keyed by the option value. An absent key (e.g. "Nvt") means no
   * toelichting field is shown for that answer. So "Nvt"/"n.v.t." options get
   * NO key.
   */
  toelichting?: Record<string, string>;
  /** Reference to the dossier section this maps to, e.g. "Luik C · Vraag 1". */
  luik?: string;
  /** Adaptive branching: only show this field when another field equals the given value. */
  showIf?: { fieldId: string; value: string };
}

export type TipTone = "tip" | "watch" | "ok";

export interface Tip {
  id: string;
  /** Tip-sheet attribution, matching the labels used in the Ethische Etna. */
  source: string;
  title: string;
  body: string;
  tone: TipTone;
  /**
   * Anchors this tip to a specific field. When set, the tip renders inline
   * directly under that input and gets an entry in the rail's overview that
   * scrolls to (and briefly highlights) the field. Tips without a fieldId are
   * general step-level guidance and stay in the rail.
   */
  fieldId?: string;
  /**
   * Makes the tip answer-aware. It shows its warning (the base title/body/tone)
   * while the anchored field holds `riskValue`, and flips to a resolved "In
   * orde" state (resolvedTitle/resolvedBody, green) for any other answer. So
   * changing the answer visibly resolves the warning.
   */
  resolve?: {
    riskValue: string;
    resolvedTitle: string;
    resolvedBody: string;
  };
}

export type FlagSeverity = "high" | "medium" | "low";

export interface PrescreenFlag {
  id: string;
  severity: FlagSeverity;
  category: string;
  title: string;
  description: string;
  source: string;
  /** Which step the reviewer jumps back to when resolving this flag. */
  stepIndex: number;
  /**
   * Ties the flag to an answer on its step. Once the field equals
   * `resolvedValue`, the flag is marked resolved in the final overview (green,
   * with the resolved copy). This closes the loop: jump to the step, change the
   * answer, and the aandachtspunt clears when you come back.
   */
  resolve?: {
    fieldId: string;
    resolvedValue: string;
    resolvedTitle: string;
    resolvedDescription: string;
  };
}

export interface IntakeStep {
  id: string;
  navLabel: string;
  eyebrow: string;
  title: string;
  intro: string;
  luik: string;
  fields: IntakeField[];
  tips: Tip[];
}

export const APPLICATION_REF = "SHW_2026_100_1";

export const STEPS: IntakeStep[] = [
  {
    id: "project",
    navLabel: "Project",
    eyebrow: "Stap 1 van 6 · Algemene informatie",
    title: "Project & route",
    intro:
      "Etna heeft je methodologie-bijlage en projectgegevens gelezen en de algemene velden alvast ingevuld. Controleer of alles klopt: je kan elk veld aanpassen.",
    luik: "Luik A · Algemene informatie",
    fields: [
      {
        id: "advies-titel",
        label: "Titel van het ethisch advies",
        type: "text",
        value: "Language preferences for ADHD people / people with ADHD",
        aiPrefilled: true,
        luik: "Luik A",
      },
      {
        id: "project",
        label: "Onderzoeksproject",
        type: "text",
        value: "R2D2-MH: Risico en veerkracht in ontwikkelingsdiversiteit en mentale gezondheid",
        aiPrefilled: true,
        luik: "Luik A",
      },
      {
        id: "commissie",
        label: "Ethische commissie",
        type: "select",
        value: "Sociale en Humane Wetenschappen (EA SHW)",
        options: [
          "Sociale en Humane Wetenschappen (EA SHW)",
          "Commissie Medische Ethiek (UZA–UAntwerpen)",
          "Commissie Wetenschap & Technologie (Dual Use)",
        ],
        hint: "Etna stelt EA SHW voor op basis van doelgroep, methode en dataverwerking.",
      },
      {
        id: "type",
        label: "Soort onderzoek",
        type: "select",
        value: "Anonieme online vragenlijst",
        options: [
          "Anonieme online vragenlijst",
          "Vragenlijst + interviews",
          "Interviews / focusgroepen",
          "Observationeel onderzoek",
          "Dossier- / hergebruik van data",
        ],
        aiPrefilled: true,
      },
      {
        id: "financiering",
        label: "Financieringsbron",
        type: "text",
        value: "Horizon Europe (grant agreement 101057385)",
        aiPrefilled: true,
        luik: "Luik A",
      },
      {
        id: "looptijd",
        label: "Verwachte looptijd",
        type: "text",
        value: "Start na goedkeuring · einde 31/12/2026",
        aiPrefilled: true,
        luik: "Luik A",
      },
    ],
    tips: [
      {
        id: "t-start",
        source: "EA SHW · Aanvraagproces",
        title: "Je onderzoek start pas na (voorlopig) positief advies",
        body: "Houd er rekening mee dat dataverzameling niet mag beginnen voordat de commissie een (voorlopig) positief ethisch advies geeft. Plan je startdatum daarna.",
        tone: "tip",
        fieldId: "looptijd",
      },
      {
        id: "t-dmp",
        source: "EA SHW · Datamanagement",
        title: "Mogelijk een datamanagementplan (DMP) nodig",
        body: "Voor projecten als dit verwacht de commissie doorgaans een DMP via DMP Online. Vink dit aan in Luik B zodat het secretariaat het niet hoeft op te vragen.",
        tone: "tip",
        fieldId: "type",
      },
    ],
  },
  {
    id: "populatie",
    navLabel: "Studieopzet",
    eyebrow: "Stap 2 van 6 · Risicoanalyse",
    title: "Studieopzet & studiepopulatie",
    intro:
      "Beschrijf kort de opzet en de beoogde deelnemers. Etna let mee op herkenbaarheid, eerlijke in-/exclusie en privacy bij werving.",
    luik: "Luik C · Vraag 1",
    fields: [
      {
        id: "opzet",
        label: "(A) Studieopzet",
        type: "textarea",
        value:
          "Anonieme vragenlijst-studie met multiple-choice en open vragen. Er worden ook demografische gegevens gevraagd (standaard, geen naam of contactinformatie die identificatie mogelijk maakt).",
        aiPrefilled: true,
      },
      {
        id: "aantal",
        label: "Geschat aantal deelnemers",
        type: "text",
        value: "± 100 (mogelijk meer bij vlotte werving)",
        aiPrefilled: true,
      },
      {
        id: "inclusie",
        label: "Inclusie- / exclusiecriteria",
        type: "textarea",
        value:
          "[1] voldoende niveau Engels (eventueel met vertaal-/schrijftools); [2] een (zelf-)diagnose ADHD; [3] ouder dan 18 jaar.",
        aiPrefilled: true,
      },
      {
        id: "werving",
        label: "Methode van rekrutering",
        type: "textarea",
        value:
          "Werving via ADHD Europe (flyers in nieuwsbrieven en sociale media) en via de kanalen van het R2D2-MH-consortium. De onderzoeker komt niet rechtstreeks in contact met mogelijke deelnemers.",
        aiPrefilled: true,
      },
    ],
    tips: [
      {
        id: "t-pop-size",
        source: "Tip Sheet 1: Studiepopulatie",
        title: "Ook in grote groepen kan iemand herkenbaar zijn",
        body: "Een (zelf-)diagnose ADHD + demografische gegevens kan in combinatie tot herkenning leiden. Houd de demografische vragen bewust grofmazig (bv. leeftijdscategorie i.p.v. exacte leeftijd).",
        tone: "watch",
        fieldId: "opzet",
      },
      {
        id: "t-pop-incl",
        source: "Tip Sheet 1: Studiepopulatie",
        title: "Toets je inclusiecriteria op eerlijkheid",
        body: "Een Engels-vereiste sluit mogelijk anderstalige deelnemers uit. Je vangt dit deels op met vertaaltools; vermeld dit expliciet zodat de exclusie onderbouwd is.",
        tone: "tip",
        fieldId: "inclusie",
      },
      {
        id: "t-pop-recruit",
        source: "Tip Sheet 1: Studiepopulatie",
        title: "Werving via een partner en anonimiteit",
        body: "Werving via de nieuwsbrief van een externe partner kan anoniem blijven zolang er geen herleidbare gegevens naar het team terugvloeien. Bevestig dat ADHD Europe geen deelnemerslijsten deelt.",
        tone: "watch",
        fieldId: "werving",
      },
    ],
  },
  {
    id: "toestemming",
    navLabel: "Toestemming",
    eyebrow: "Stap 3 van 6 · Risicoanalyse",
    title: "Toestemming & anonimiteit",
    intro:
      "De kern van het dossier: geïnformeerde toestemming en de anonimiteitsclaim. Etna vergelijkt je antwoorden met je werving en je informed consent-bijlage.",
    luik: "Luik B · 1 + Luik C · Vragen 2–4",
    fields: [
      {
        id: "databeheer",
        label: "Databeheer conform GDPR/AVG bevestigd?",
        type: "radio",
        value: "Ja",
        options: ["Ja", "Nee"],
        toelichting: {
          Ja: "De data worden verwerkt volgens de GDPR/AVG-principes: dataminimalisatie, veilige opslag op UAntwerpen-infrastructuur en een duidelijke bewaartermijn.",
          Nee: "Het databeheer wijkt af van de GDPR/AVG; hieronder lichten we toe welke maatregelen ontbreken en hoe we dit rechtzetten.",
        },
        luik: "Luik B · 1",
      },
      {
        id: "ethische-consent",
        label: "Wijk je af van actieve schriftelijke geïnformeerde toestemming?",
        type: "radio",
        value: "Nee",
        options: ["Nee", "Ja"],
        toelichting: {
          Nee: "Toestemming wordt actief bekomen via een geschreven online toestemmingsformulier (zie bijlage).",
          Ja: "We wijken af van actieve schriftelijke toestemming; hieronder motiveren we waarom en welke alternatieve procedure we gebruiken.",
        },
        luik: "Luik C · Vraag 2",
      },
      {
        id: "wettelijke-consent",
        label: "Verzamel of gebruik je gegevens die individuen herkenbaar maken?",
        type: "radio",
        value: "Nee",
        options: ["Nee", "Ja"],
        toelichting: {
          Nee: "Er worden op geen enkel moment identiteitsgegevens verzameld.",
          Ja: "We verzamelen gegevens die individuen herkenbaar maken; hieronder beschrijven we welke en op welke GDPR-grondslag.",
        },
        luik: "Luik C · Vraag 3",
      },
      {
        id: "herkenbaarheid",
        label: "Kunnen personen geïdentificeerd worden in analyse-data of resultaten?",
        type: "radio",
        value: "Nee",
        options: ["Nee", "Ja"],
        toelichting: {
          Nee: "Het onderzoek is anoniem.",
          Ja: "Personen kunnen herkenbaar zijn in de analyse-data; hieronder leggen we uit welke maatregelen we nemen en welke toestemming daarbij hoort.",
        },
        luik: "Luik C · Vraag 4",
      },
      {
        id: "tool-metadata",
        label: "Bevestig: de online survey-tool bewaart geen IP-adres of e-mail",
        type: "radio",
        value: "Nog te bevestigen",
        options: ["Nog te bevestigen", "Bevestigd"],
        hint: "Zet in je survey-platform het loggen van IP en e-mail uit. Dit onderbouwt je anonimiteitsclaim (voorafcheck-punt).",
        toelichting: {
          "Nog te bevestigen":
            "We moeten in het survey-platform nog nagaan en uitschakelen dat IP-adres en e-mail worden gelogd.",
          Bevestigd:
            "In het survey-platform is het loggen van IP-adres en e-mail uitgeschakeld, waardoor er geen herleidbare metadata bewaard blijft.",
        },
        luik: "Luik C · Vraag 4",
      },
      {
        id: "consent-leesbaar",
        label: "Consentformulier gecontroleerd op leesbaarheid voor de doelgroep?",
        type: "radio",
        value: "Nog te doen",
        options: ["Nog te doen", "Gecontroleerd"],
        hint: "Kort en helder, afgestemd op ADHD en mogelijk vertaaltool-gebruik. De verplichte inhoud staat al in de ENG-bijlage (voorafcheck-punt).",
        toelichting: {
          "Nog te doen":
            "We moeten het consentformulier nog nalezen op leesbaarheid voor de doelgroep.",
          Gecontroleerd:
            "Het consentformulier is nagelezen op leesbaarheid: kort, helder en afgestemd op ADHD en mogelijk vertaaltool-gebruik.",
        },
        luik: "Luik B · 1",
      },
    ],
    tips: [
      {
        id: "t-consent-template",
        source: "Tip Sheet 2 & 3: Informed consent",
        title: "Pas de taal aan, behoud de verplichte inhoud",
        body: "Gebruik het EA SHW-template en stem de taal af op de doelgroep. Onvolledige, foutieve of overbodige info in het consentformulier leidt tot een voorlopig negatief advies.",
        tone: "tip",
        fieldId: "ethische-consent",
      },
      {
        id: "t-anon-claim",
        source: "Tip Sheet 4: Anonimiteit",
        title: "Herkenbaarheid botst met je anonimiteitsclaim",
        body: "Je gaf aan dat personen herkenbaar kunnen zijn in de analyse-data of resultaten. Dan is de studie niet anoniem: je hebt een GDPR-grondslag en aangepaste toestemming nodig. Anonimiseer verder of pas je claim aan.",
        tone: "watch",
        fieldId: "herkenbaarheid",
        resolve: {
          riskValue: "Ja",
          resolvedTitle: "Geen identificatie mogelijk: anonimiteit onderbouwd",
          resolvedBody:
            "Je koos ‘Nee’: personen zijn niet herkenbaar in je resultaten. Dat sluit aan bij je anonimiteitsclaim, dus een aparte GDPR-toestemming is hier niet nodig.",
        },
      },
      {
        id: "t-anon-flow",
        source: "Flowchart: Is mijn studie anoniem?",
        title: "Herleidbare gegevens vragen om GDPR-toestemming",
        body: "Je gaf aan dat je gegevens verzamelt die individuen herkenbaar maken. Dan volstaat ethische toestemming niet en heb je een GDPR-grondslag met aangepaste consent nodig.",
        tone: "watch",
        fieldId: "wettelijke-consent",
        resolve: {
          riskValue: "Ja",
          resolvedTitle: "Geen herleidbare gegevens: ethische toestemming volstaat",
          resolvedBody:
            "Je koos ‘Nee’. Zolang je écht geen herleidbare gegevens (ook geen metadata zoals IP of e-mail) verzamelt, volstaat ethische toestemming en is GDPR-consent niet nodig.",
        },
      },
    ],
  },
  {
    id: "kwetsbaarheid",
    navLabel: "Kwetsbaarheid",
    eyebrow: "Stap 4 van 6 · Risicoanalyse",
    title: "Vergoeding, minderjarigen & kwetsbaarheid",
    intro:
      "Korte triage van risico-thema’s. De meeste staan op ‘Nvt/Nee’ voor deze studie. Etna wijst op één aandachtspunt rond de doelgroep.",
    luik: "Luik C · Vragen 5–11",
    fields: [
      {
        id: "vergoeding",
        label: "Bied je vergoedingen of extra’s?",
        type: "radio",
        value: "Nvt",
        options: ["Nvt", "Nee", "Ja"],
        toelichting: {
          Nee: "Er is geen vergoeding voor deelnemers.",
          Ja: "Deelnemers krijgen een vergoeding of extra's; hieronder beschrijven we het bedrag of de aard en hoe we ongepaste beïnvloeding vermijden.",
        },
        luik: "Luik C · Vraag 5–6",
      },
      {
        id: "minderjarigen",
        label: "Reken je deelnemers jonger dan 18 jaar?",
        type: "radio",
        value: "Nvt",
        options: ["Nvt", "Nee", "Ja"],
        toelichting: {
          Nee: "Alle deelnemers zijn 18 of ouder.",
          Ja: "Er nemen deelnemers jonger dan 18 deel; hieronder beschrijven we de ouderlijke toestemming en de aangepaste procedure.",
        },
        luik: "Luik C · Vraag 7–8",
      },
      {
        id: "minderjarigen-toestemming",
        label: "Zijn er ouderlijke toestemmingsformulieren voorzien?",
        type: "radio",
        value: "Nee",
        options: ["Ja: template bijgevoegd", "Nee: nog te regelen"],
        showIf: { fieldId: "minderjarigen", value: "Ja" },
        hint: "Bij deelnemers jonger dan 18 is toestemming van de wettelijke voogd verplicht. Gebruik het EA SHW-template.",
        toelichting: {
          "Ja: template bijgevoegd":
            "Het EA SHW-template voor ouderlijke toestemming is ingevuld en als bijlage toegevoegd.",
          "Nee: nog te regelen":
            "De ouderlijke toestemmingsformulieren zijn nog niet voorzien; we regelen dit vóór de start van de dataverzameling.",
        },
        luik: "Luik C · Vraag 7",
      },
      {
        id: "kwetsbaar-vooraf",
        label: "Werk je met deelnemers die vooraf erg kwetsbaar zijn?",
        type: "radio",
        value: "Nee",
        options: ["Nee", "Ja"],
        toelichting: {
          Nee: "De deelnemers zijn vooraf niet bijzonder kwetsbaar; volwassenen met ADHD vormen op zich geen kwetsbare groep.",
          Ja: "De deelnemers zijn vooraf al kwetsbaar; hieronder beschrijven we welke bescherming en ondersteuning we voorzien.",
        },
        luik: "Luik C · Vraag 9",
      },
      {
        id: "kwetsbaar-deelname",
        label: "Worden deelnemers kwetsbaar(der) door deelname?",
        type: "radio",
        value: "Nee",
        options: ["Nee", "Ja"],
        toelichting: {
          Nee: "Deelname aan de anonieme vragenlijst maakt deelnemers niet kwetsbaarder; de vragen zijn laagdrempelig en vrijwillig.",
          Ja: "Deelname kan deelnemers kwetsbaarder maken; hieronder beschrijven we de risico's en de doorverwijzing naar ondersteuning.",
        },
        luik: "Luik C · Vraag 10",
      },
      {
        id: "kwetsbaar-onderzoeker",
        label: "Kunnen onderzoekers kwetsbaar worden door de studie?",
        type: "radio",
        value: "Nee",
        options: ["Nee", "Ja"],
        toelichting: {
          Nee: "De onderzoekers lopen geen bijzonder risico; het gaat om een anonieme online survey zonder veldwerk in een risicovolle context.",
          Ja: "De onderzoekers kunnen kwetsbaar worden door de studie; hieronder beschrijven we de context en de genomen maatregelen.",
        },
        luik: "Luik C · Vraag 11",
      },
    ],
    tips: [
      {
        id: "t-vuln-label",
        source: "Tip Sheet 9 & 10: Kwetsbaarheid",
        title: "Label niet te snel, maar reflecteer wel",
        body: "Mensen met ADHD zijn niet per definitie ‘kwetsbaar’. Toch peilt je vragenlijst naar stigma; denk na of die vragen ongewenste reacties kunnen oproepen en of je naar ondersteuning kan doorverwijzen.",
        tone: "watch",
        fieldId: "kwetsbaar-vooraf",
      },
      {
        id: "t-reimb",
        source: "Tip Sheet 5 & 6: Vergoeding",
        title: "Geen vergoeding: niets verder nodig",
        body: "Omdat je geen vergoeding voorziet, vervallen de vragen over ongepaste beïnvloeding en koppeling van contactgegevens. Goed gekozen voor een laagdrempelige, anonieme survey.",
        tone: "ok",
        fieldId: "vergoeding",
      },
    ],
  },
  {
    id: "context",
    navLabel: "Opname & context",
    eyebrow: "Stap 5 van 6 · Risicoanalyse",
    title: "Opnames, hergebruik & samenwerkingscontext",
    intro:
      "De laatste risicovragen plus de samenwerkingscontext. Etna merkt je co-creatiegroep op en vraagt je dat even te bevestigen.",
    luik: "Luik B · 2 + Luik C · Vragen 12–16",
    fields: [
      {
        id: "opnames",
        label: "Maak of bewaar je foto’s, audio- of video-opnames?",
        type: "radio",
        value: "Nvt",
        options: ["Nvt", "Nee", "Ja"],
        toelichting: {
          Nee: "Er worden geen foto's, audio- of video-opnames gemaakt of bewaard.",
          Ja: "Er worden opnames gemaakt of bewaard; hieronder beschrijven we de bewaring, de toegang en het moment van vernietiging.",
        },
        luik: "Luik C · Vraag 12",
      },
      {
        id: "opnames-bewaring",
        label: "Hoe worden de opnames bewaard en wanneer vernietigd?",
        type: "textarea",
        value: "",
        showIf: { fieldId: "opnames", value: "Ja" },
        hint: "Beschrijf wie toegang heeft tot de ruwe opnames, de bewaartermijn en het moment van vernietiging. Opnames zijn zelden volledig anonimiseerbaar.",
        luik: "Luik C · Vraag 12",
      },
      {
        id: "misleiding",
        label: "Misleid je deelnemers zonder debriefing achteraf?",
        type: "radio",
        value: "Nvt",
        options: ["Nvt", "Nee", "Ja"],
        toelichting: {
          Nee: "Deelnemers worden niet misleid; het onderzoek is volledig transparant over doel en inhoud.",
          Ja: "Er is sprake van misleiding; hieronder beschrijven we de verplichte debriefing en hoe deelnemers hun data kunnen intrekken.",
        },
        luik: "Luik C · Vraag 13",
      },
      {
        id: "debriefing",
        label: "Beschrijf je debriefing-procedure",
        type: "textarea",
        value: "",
        showIf: { fieldId: "misleiding", value: "Ja" },
        hint: "Bij deception is debriefing verplicht: leg uit hoe je deelnemers achteraf volledig informeert en hoe ze hun data nog kunnen intrekken.",
        luik: "Luik C · Vraag 13",
      },
      {
        id: "hergebruik-later",
        label: "Zal je niet-anonieme data later voor andere doeleinden gebruiken?",
        type: "radio",
        value: "Nee",
        options: ["Nee", "Ja"],
        toelichting: {
          Nee: "Enkel anonieme data kunnen later hergebruikt worden (standaard in het consentformulier).",
          Ja: "We hergebruiken later niet-anonieme data voor andere doeleinden; hieronder beschrijven we het doel en de GDPR-grondslag.",
        },
        luik: "Luik C · Vraag 14–15",
      },
      {
        id: "externe-partner",
        label: "Werk je samen met een externe partner die deelnemers werft?",
        type: "radio",
        value: "Ja",
        options: ["Ja", "Nee"],
        toelichting: {
          Ja: "Een management executive van ADHD-Europe helpt bij de werving; privacybeleid voldoet aan AVG/GDPR en EA SHW-richtlijnen.",
          Nee: "We werken niet samen met een externe partner die deelnemers werft; de werving verloopt volledig via het eigen onderzoeksteam.",
        },
        luik: "Luik B · 2",
      },
      {
        id: "machtsonevenwicht",
        label: "Is er een mogelijke context van ongelijkheid of machtsonevenwicht?",
        type: "radio",
        value: "Nee",
        options: ["Nee", "Ja"],
        toelichting: {
          Nee: "Er is geen context van ongelijkheid of machtsonevenwicht; deelname is anoniem, vrijwillig en zonder afhankelijkheidsrelatie.",
          Ja: "Er is een mogelijk machtsonevenwicht; hieronder beschrijven we de relatie en hoe we vrijwilligheid en gelijkwaardigheid borgen.",
        },
        luik: "Luik C · Vraag 16",
      },
    ],
    tips: [
      {
        id: "t-global",
        source: "Tip Sheet 16: Global engagement",
        title: "Je werkt samen met een co-creatiegroep",
        body: "Je methodologie vermeldt een co-creatiegroep van neurodivergente mensen. Dat is sterk participatief onderzoek; overweeg kort te beschrijven hoe gelijkwaardigheid en zeggenschap zijn ingebouwd, ook al antwoord je ‘Nee’ op Vraag 16.",
        tone: "watch",
        fieldId: "machtsonevenwicht",
      },
      {
        id: "t-reuse",
        source: "Tip Sheet 14 & 15: Hergebruik van data",
        title: "Anoniem hergebruik is standaard gedekt",
        body: "Omdat enkel anonieme data later hergebruikt worden, volstaat de standaardmelding in het consentformulier. Geen aparte GDPR-grondslag nodig.",
        tone: "ok",
        fieldId: "hergebruik-later",
      },
    ],
  },
  {
    id: "indienen",
    navLabel: "Nakijken",
    eyebrow: "Stap 6 van 6 · Voorafcheck & indienen",
    title: "Bijlagen, voorafcheck & indienen",
    intro:
      "Etna voert een voorafcheck uit op je volledige dossier. Deze check houdt je aanvraag niet tegen: je kan aanpassen, kort toelichten, of het secretariaat laten meekijken.",
    luik: "Luik D + Luik E",
    fields: [],
    tips: [],
  },
];

/**
 * Documents the researcher is asked to add at the Phase 0 onboarding gate,
 * before the AI is allowed to read anything. Each carries a tip-sheet-grounded
 * reason ("why this is needed") so the upload step teaches as it collects.
 * The two required documents map 1:1 to ATTACHMENTS below.
 */
export interface IntakeDocument {
  id: string;
  label: string;
  required: boolean;
  /** Plain-language, grounded reason this document is requested. */
  why: string;
  /** Short helper line: accepted formats / what the document is. */
  hint: string;
  /** Filename used by the demo's "load example documents" shortcut. */
  exampleFile?: string;
}

export const INTAKE_DOCUMENTS: IntakeDocument[] = [
  {
    id: "methodologie",
    label: "Methodologie-bijlage",
    required: true,
    why: "Verplichte bijlage (Luik D). Etna leest hieruit je opzet, populatie en werving om de aanvraag alvast in te vullen.",
    hint: "PDF of Word · de beschrijving van je studieopzet",
    exampleFile: "Att_1_-_Methodologie.pdf",
  },
  {
    id: "consent",
    label: "Informed consent-formulier",
    required: true,
    why: "Verplichte bijlage. Onvolledige of foutieve consent is de #1 oorzaak van herwerk (Tip Sheet 2 & 3).",
    hint: "PDF of Word · het toestemmingsformulier voor deelnemers",
    exampleFile: "Informed_Consent_Form_ENG.pdf",
  },
  {
    id: "dmp",
    label: "Datamanagementplan (DMP)",
    required: false,
    why: "Aanbevolen. Voor onderzoek met persoonsgegevens verwacht de commissie meestal een DMP; voeg toe zodat het secretariaat het niet hoeft op te vragen.",
    hint: "Optioneel · bv. geëxporteerd uit DMP Online",
  },
  {
    id: "instrument",
    label: "Vragenlijst of onderzoeksinstrument",
    required: false,
    why: "Optioneel maar nuttig. Hiermee schat Etna je anonimiteits- en kwetsbaarheidsvragen scherper in (Tip Sheet 1).",
    hint: "Optioneel · de survey, interviewleidraad of itemlijst",
  },
  {
    id: "werving",
    label: "Wervingsmateriaal",
    required: false,
    why: "Optioneel. Laat Etna de wervingsroute en anonimiteit bij externe partners controleren (Tip Sheet 1).",
    hint: "Optioneel · flyers, nieuwsbriefteksten of social posts",
  },
];

export interface AttachmentItem {
  name: string;
  label: string;
  status: "ready" | "missing";
  note: string;
}

export const ATTACHMENTS: AttachmentItem[] = [
  {
    name: "Att_1_-_Methodologie.pdf",
    label: "Methodologie",
    status: "ready",
    note: "Verplichte bijlage · aanwezig",
  },
  {
    name: "Informed_Consent_Form_ENG.pdf",
    label: "Informed consent (anoniem, ENG)",
    status: "ready",
    note: "Verplichte bijlage · aanwezig",
  },
];

/**
 * Resolves a tip against the current answer of its field. Answer-aware tips
 * (those with a `resolve` block) show their warning while the field holds the
 * risk value, and flip to a green "In orde" state for any other answer. Shared
 * by the form column's inline tips and the sidekick's tip overview so a tip
 * reads the same wherever it appears.
 */
export function resolveTip(
  tip: Tip,
  value: string | undefined,
): { tone: TipTone; title: string; body: string; resolved: boolean } {
  if (tip.resolve && value !== tip.resolve.riskValue) {
    return {
      tone: "ok",
      title: tip.resolve.resolvedTitle,
      body: tip.resolve.resolvedBody,
      resolved: true,
    };
  }
  return { tone: tip.tone, title: tip.title, body: tip.body, resolved: false };
}

/** Shared tone → color/label/icon mapping for guide tips. */
export function tipVisuals(tone: TipTone) {
  if (tone === "watch") {
    return {
      accent: "var(--ua-warning)",
      border: "#f0dcae",
      bg: "#fdf6e9",
      label: "Let op",
      icon: "!",
    };
  }
  if (tone === "ok") {
    return {
      accent: "var(--ua-success)",
      border: "#a3e4c1",
      bg: "#edfaf2",
      label: "In orde",
      icon: "✓",
    };
  }
  return {
    accent: "var(--ua-blue)",
    border: "#c5d0e8",
    bg: "#eef4fb",
    label: "Tip",
    icon: "i",
  };
}

export const PRESCREEN_FLAGS: PrescreenFlag[] = [
  {
    id: "f-anon",
    severity: "medium",
    category: "Anonimiteit",
    title: "Bevestig dat de online tool geen herleidbare metadata bewaart",
    description:
      "Je claimt een volledig anonieme studie. Survey-platformen loggen soms IP-adressen of e-mail. Bevestig dat dit is uitgeschakeld, zodat de anonimiteitsclaim houdbaar blijft.",
    source: "Tip Sheet 4 — Anonimiteit",
    stepIndex: 2,
    resolve: {
      fieldId: "tool-metadata",
      resolvedValue: "Bevestigd",
      resolvedTitle: "Anonimiteit bevestigd: tool bewaart geen metadata",
      resolvedDescription:
        "Je bevestigde dat de online tool geen IP-adres of e-mail logt. De anonimiteitsclaim is daarmee onderbouwd.",
    },
  },
  {
    id: "f-consent-lang",
    severity: "medium",
    category: "Informed consent",
    title: "Stem de leesbaarheid van het consentformulier af op de doelgroep",
    description:
      "De doelgroep gebruikt mogelijk vertaaltools en heeft ADHD. Houd het formulier kort en helder. De verplichte inhoud is aanwezig in de ENG-bijlage; controleer enkel de toegankelijkheid.",
    source: "Tip Sheet 2 & 3 — Informed consent",
    stepIndex: 2,
    resolve: {
      fieldId: "consent-leesbaar",
      resolvedValue: "Gecontroleerd",
      resolvedTitle: "Leesbaarheid consentformulier gecontroleerd",
      resolvedDescription:
        "Je gaf aan het consentformulier te hebben gecontroleerd op toegankelijkheid voor de doelgroep (kort, helder, vertaaltool-vriendelijk).",
    },
  },
  {
    id: "f-partner",
    severity: "low",
    category: "Werving",
    title: "Externe partner: verklaring aanwezig",
    description:
      "De wervingspartner (ADHD-Europe) is benoemd met de vereiste AVG/GDPR-verklaring. Geen verdere actie nodig; opgenomen ter bevestiging.",
    source: "Tip Sheet 1 — Studiepopulatie",
    stepIndex: 4,
  },
  {
    id: "f-cocreation",
    severity: "low",
    category: "Samenwerking",
    title: "Co-creatiegroep: overweeg een korte toelichting bij Vraag 16",
    description:
      "Je werkt participatief met neurodivergente co-onderzoekers. Een zin over hoe gelijkwaardigheid is geborgd, voorkomt vragen van de commissie achteraf.",
    source: "Tip Sheet 16 — Global engagement",
    stepIndex: 4,
  },
];
